import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { RETURN_ADDRESS, CONTACT_INFO, siteImages } from '../data/mock';
import { MapPin, Package, RefreshCw, Clock, CheckCircle, XCircle, Mail, Phone, ArrowRight } from 'lucide-react';

const ReturnsPage = () => {
  useEffect(() => {
    document.title = 'Returns & Refund Policy | Clarity Lens Co';
  }, []);

  return (
    <div className="min-h-screen bg-cream-50">
      <Header />
      <CartDrawer />

      <main className="pt-[116px] lg:pt-[136px]">
        {/* Editorial Banner */}
        <section className="relative">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-10 lg:pt-16 pb-10 lg:pb-16">
            <div className="grid lg:grid-cols-12 gap-8 items-end">
              <div className="lg:col-span-7">
                <span className="text-eyebrow text-copper-600 font-mono">— Easy Returns</span>
                <h1 className="font-display text-ink-900 text-display-2xl mt-4">
                  Returns &amp; <span className="italic font-light">refunds.</span>
                </h1>
                <p className="mt-5 text-ink-500 text-lg max-w-xl">
                  Not quite right? Send it back within 30 days for a full refund — it&rsquo;s that simple.
                </p>
              </div>
              <div className="lg:col-span-5">
                <div className="aspect-[16/9] lg:aspect-[5/6] overflow-hidden bg-cream-100">
                  <img src={siteImages.returnsBanner} alt="Returns &amp; refunds" className="w-full h-full object-cover" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main */}
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 pb-24">
          {/* Return Address - prominent editorial block */}
          <div className="bg-ink-900 text-cream-50 p-10 lg:p-14 mb-16 relative overflow-hidden">
            <div className="grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8">
                <div className="flex items-center gap-3 mb-5">
                  <MapPin className="w-5 h-5 text-copper-400" />
                  <span className="text-eyebrow text-copper-400 font-mono">Ship returns to</span>
                </div>
                <p className="font-display text-4xl lg:text-5xl leading-[1.1]">
                  {RETURN_ADDRESS.street}
                </p>
                <p className="font-display text-2xl text-cream-200 mt-3">
                  {RETURN_ADDRESS.city}, {RETURN_ADDRESS.state} {RETURN_ADDRESS.zip}
                </p>
                <p className="text-cream-200/60 mt-2 text-sm tracking-wider uppercase font-mono">
                  {RETURN_ADDRESS.country}
                </p>
              </div>
              <div className="lg:col-span-4 lg:text-right">
                <span className="font-display text-[120px] italic font-light text-copper-400/30 leading-none select-none">30d</span>
              </div>
            </div>
          </div>

          {/* Policy cards */}
          <div className="grid sm:grid-cols-3 gap-px bg-ink-200 mb-16">
            {[
              { icon: Clock, title: '30-Day Window', desc: 'Return within 30 days of delivery.' },
              { icon: Package, title: 'Original Condition', desc: 'Unused items with original packaging.' },
              { icon: RefreshCw, title: 'Full Refund', desc: 'Refunded to your original payment.' },
            ].map((item, i) => (
              <div key={i} className="bg-cream-50 p-8 text-center">
                <item.icon className="w-6 h-6 text-copper-600 mx-auto mb-4" strokeWidth={1.2} />
                <h3 className="font-display text-xl text-ink-900 mb-2">{item.title}</h3>
                <p className="text-sm text-ink-500">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Eligible / Not eligible */}
          <div className="grid md:grid-cols-2 gap-10 mb-20">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <CheckCircle className="w-5 h-5 text-copper-600" />
                <h3 className="font-display text-2xl text-ink-900">Eligible for return</h3>
              </div>
              <ul className="space-y-4">
                {[
                  'Unopened items in original packaging',
                  'Defective or damaged products',
                  'Wrong item received',
                  'Items within 30 days of delivery',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-ink-600 border-b border-ink-100 pb-4">
                    <span className="w-6 h-6 border border-copper-600 text-copper-600 flex items-center justify-center text-xs flex-shrink-0 mt-0.5">
                      {i + 1}
                    </span>
                    <span className="text-sm leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="flex items-center gap-3 mb-6">
                <XCircle className="w-5 h-5 text-ink-500" />
                <h3 className="font-display text-2xl text-ink-900">Not eligible</h3>
              </div>
              <ul className="space-y-4">
                {[
                  'Used or worn items',
                  'Items without original packaging',
                  'Items beyond 30-day return window',
                  'Items damaged through misuse',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-ink-500 border-b border-ink-100 pb-4">
                    <span className="w-6 h-6 border border-ink-300 text-ink-400 flex items-center justify-center text-xs flex-shrink-0 mt-0.5">
                      {i + 1}
                    </span>
                    <span className="text-sm leading-relaxed line-through decoration-ink-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* How to return */}
          <div className="mb-20">
            <div className="grid lg:grid-cols-12 gap-8 mb-10">
              <div className="lg:col-span-4">
                <span className="text-eyebrow text-copper-600 font-mono">— The process</span>
                <h2 className="font-display text-display-lg text-ink-900 mt-3">How to return.</h2>
              </div>
              <p className="lg:col-span-6 lg:col-start-6 text-ink-500 text-lg leading-relaxed">
                Follow these five simple steps and we&rsquo;ll process your refund as quickly as possible.
              </p>
            </div>
            <div className="space-y-6">
              {[
                { title: 'Contact us', desc: `Email ${CONTACT_INFO.email} with your order number and reason for return.` },
                { title: 'Get authorization', desc: 'We’ll reply with a return authorization and shipping instructions.' },
                { title: 'Pack securely', desc: 'Repack items in original packaging with all tags and accessories.' },
                { title: 'Ship to us', desc: 'Send to our return address and keep the tracking number.' },
                { title: 'Receive refund', desc: 'Refund is processed within 5–7 business days after we receive your return.' },
              ].map((item, i) => (
                <div key={i} className="grid lg:grid-cols-12 gap-4 py-6 border-t border-ink-200">
                  <div className="lg:col-span-2">
                    <span className="font-display text-4xl italic font-light text-copper-600">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <div className="lg:col-span-10">
                    <h4 className="font-display text-2xl text-ink-900 mb-2">{item.title}</h4>
                    <p className="text-ink-500 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="bg-cream-100 p-10 lg:p-16 text-center">
            <h3 className="font-display text-3xl text-ink-900 mb-3">Need help with a return?</h3>
            <p className="text-ink-500 mb-8 max-w-md mx-auto">
              Our customer support team is ready to assist you with any questions.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href={`mailto:${CONTACT_INFO.email}`}
                className="flex items-center gap-2 px-8 py-3 bg-ink-900 text-cream-50 hover:bg-copper-600 transition-colors"
              >
                <Mail className="w-4 h-4" />
                <span className="text-sm tracking-[0.15em] uppercase font-mono">Email us</span>
              </a>
              <a
                href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
                className="flex items-center gap-2 px-8 py-3 border border-ink-300 text-ink-900 hover:border-ink-900 transition-colors"
              >
                <Phone className="w-4 h-4" />
                <span className="text-sm tracking-[0.15em] uppercase font-mono">{CONTACT_INFO.phone}</span>
              </a>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ReturnsPage;
